<template>
  <div class="ops-page dispatch-page">
    <!-- Header -->
    <div class="ops-head">
      <div class="ops-head__left">
        <div class="ops-kicker">Ops Portal</div>
        <h2 class="ops-title">Dispatch</h2>
        <p class="ops-sub">Packaging, labeling, and product proof. Upload docs, then mark dispatch done.</p>
      </div>

      <div class="ops-head__right">
        <button class="btn" @click="refreshAll" :disabled="loadingQueue || loadingDetail">
          {{ (loadingQueue || loadingDetail) ? 'Refreshing…' : 'Refresh' }}
        </button>
      </div>
    </div>

    <div class="ops-grid">
      <!-- Left: Queue -->
      <div class="card queue-card">
        <div class="card-head">
          <div>
            <div class="card-title">Dispatch Queue</div>
            <div class="card-sub">Orders in <span class="pill">DISPATCH_PENDING</span></div>
          </div>

          <div class="queue-tools">
            <input
              v-model="search"
              class="inp"
              placeholder="Search name / phone / lead code"
              @keyup.enter="loadQueue"
            />
            <button class="btn btn--soft" @click="loadQueue" :disabled="loadingQueue">
              {{ loadingQueue ? 'Loading…' : 'Search' }}
            </button>
          </div>
        </div>

        <div v-if="queueError" class="alert alert--danger">{{ queueError }}</div>

        <div class="queue-list">
          <button
            v-for="o in orders"
            :key="o.id"
            class="queue-item"
            :class="selected && selected.id===o.id ? 'queue-item--active' : ''"
            @click="selectOrder(o)"
          >
            <div class="qi-top">
              <div class="qi-id">#{{ o.id }}</div>
              <div class="qi-stage">{{ o.stage }}</div>
            </div>
            <div class="qi-name">{{ o.customerName || 'Customer' }}</div>
            <div class="qi-meta">
              <span class="qi-chip">{{ o.leadCode || '—' }}</span>
              <span class="qi-chip">{{ o.customerPhone || '—' }}</span>
            </div>
            <div class="qi-time">Updated: {{ fmtDT(o.updatedAt || o.createdAt) }}</div>
          </button>

          <div v-if="!loadingQueue && orders.length===0" class="empty">
            <div class="empty-title">No orders waiting for Dispatch 🎁</div>
            <div class="empty-sub">Once Finance receives ADVANCE_50, orders appear here.</div>
          </div>
        </div>
      </div>

      <!-- Right: Detail -->
      <div class="card detail-card">
        <div class="card-head">
          <div>
            <div class="card-title">Order Detail</div>
            <div class="card-sub">Upload dispatch docs and mark done.</div>
          </div>

          <div v-if="selected" class="detail-actions">
            <button
              class="btn btn--primary"
              @click="markDispatchDone"
              :disabled="markingDone || !canMarkDone"
              :title="markDoneHint"
            >
              {{ markingDone ? 'Marking…' : 'Mark Dispatch Done' }}
            </button>
          </div>
        </div>

        <div v-if="detailError" class="alert alert--danger">{{ detailError }}</div>

        <div v-if="!selected" class="empty">
          <div class="empty-title">Select an order from the queue</div>
          <div class="empty-sub">You’ll see docs, uploads, and completion controls here.</div>
        </div>

        <div v-else>
          <!-- Summary -->
          <div class="order-summary">
            <div class="os-row">
              <div class="os-label">Order ID</div>
              <div class="os-value">#{{ selected.id }}</div>
            </div>
            <div class="os-row">
              <div class="os-label">Stage</div>
              <div class="os-value">
                <span class="pill pill--stage">
                  {{ detailStage }}
                </span>
              </div>
            </div>
            <div class="os-row">
              <div class="os-label">Customer</div>
              <div class="os-value">{{ selected.customerName || '—' }}</div>
            </div>
            <div class="os-row">
              <div class="os-label">Phone</div>
              <div class="os-value">{{ selected.customerPhone || '—' }}</div>
            </div>
            <div class="os-row">
              <div class="os-label">Lead Code</div>
              <div class="os-value">{{ selected.leadCode || '—' }}</div>
            </div>
          </div>

          <!-- Upload -->
          <div class="section">
            <div class="section-head">
              <div class="section-title">Upload Dispatch Docs</div>
              <div class="section-sub">Packaging, labeling, and product images.</div>
            </div>

            <div class="upload-grid">
              <div class="field">
                <label class="lbl">Doc Type</label>
                <select v-model="upload.docType" class="inp">
                  <option value="PACKAGING_DOC">PACKAGING_DOC</option>
                  <option value="LABELING_DOC">LABELING_DOC</option>
                  <option value="PRODUCT_IMAGE">PRODUCT_IMAGE</option>
                  <option value="OTHER">OTHER</option>
                </select>
              </div>

              <div class="field">
                <label class="lbl">Note (optional)</label>
                <input v-model="upload.note" class="inp" placeholder="e.g., carton count, fragile notes…" />
              </div>

              <div class="field field--file">
                <label class="lbl">File</label>
                <input type="file" @change="onPickFile" />
                <div class="file-meta" v-if="upload.fileName">
                  <span class="file-chip">{{ upload.fileName }}</span>
                  <span class="file-chip">{{ prettyBytes(upload.byteSize) }}</span>
                </div>
              </div>

              <div class="field field--btn">
                <button class="btn btn--primary" @click="uploadDoc" :disabled="uploading || !upload.dataUrl || !selected">
                  {{ uploading ? 'Uploading…' : 'Upload' }}
                </button>
              </div>
            </div>

            <div v-if="uploadError" class="alert alert--danger">{{ uploadError }}</div>
            <div v-if="uploadOk" class="alert alert--ok">{{ uploadOk }}</div>
          </div>

          <!-- Docs list -->
          <div class="section">
            <div class="section-head">
              <div class="section-title">Dispatch Files</div>
              <div class="section-sub">At least one dispatch doc is required before marking done.</div>
            </div>

            <div v-if="docsLoading" class="muted">Loading docs…</div>

            <div v-else>
              <div v-if="dispatchDocs.length===0" class="empty small">
                <div class="empty-title">No dispatch docs uploaded yet</div>
                <div class="empty-sub">Upload at least one to enable “Mark Dispatch Done”.</div>
              </div>

              <div v-else class="docs-list">
                <div v-for="d in dispatchDocs" :key="d.id" class="doc-row">
                  <div class="doc-left">
                    <div class="doc-title">
                      <span class="doc-pill">{{ d.docType }}</span>
                      <span class="doc-name">{{ d.fileName }}</span>
                    </div>
                    <div class="doc-meta">
                      <span>{{ prettyBytes(d.byteSize) }}</span>
                      <span class="dot">•</span>
                      <span>{{ fmtDT(d.createdAt) }}</span>
                      <span v-if="d.note" class="dot">•</span>
                      <span v-if="d.note">{{ d.note }}</span>
                    </div>
                  </div>
                  <div class="doc-right">
                    <button class="btn btn--soft" @click="downloadDoc(d)">Download</button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Helpful state -->
          <div class="section">
            <div class="section-head">
              <div class="section-title">Completion</div>
              <div class="section-sub">
                Mark done once packaging and labeling is completed. Finance will be notified for SECOND_40.
              </div>
            </div>

            <div class="completion-box">
              <div class="completion-row">
                <div class="completion-label">Docs uploaded</div>
                <div class="completion-value">
                  <span :class="dispatchDocs.length>0 ? 'ok' : 'bad'">
                    {{ dispatchDocs.length }}
                  </span>
                </div>
              </div>
              <div class="completion-row">
                <div class="completion-label">Ready to mark done</div>
                <div class="completion-value">
                  <span :class="canMarkDone ? 'ok' : 'bad'">
                    {{ canMarkDone ? 'YES' : 'NO' }}
                  </span>
                </div>
              </div>

              <div class="completion-row completion-row--btn">
                <button
                  class="btn btn--primary"
                  @click="markDispatchDone"
                  :disabled="markingDone || !canMarkDone"
                  :title="markDoneHint"
                >
                  {{ markingDone ? 'Marking…' : 'Mark Dispatch Done' }}
                </button>
              </div>

              <div class="muted" style="margin-top:8px;">
                Backend rules: stage must be <b>DISPATCH_PENDING</b> and at least one <b>DISPATCH</b> doc must exist.
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>

  </div>
</template>

<script>
export default {
  name: 'OpsDispatch',
  data () {
    return {
      search: '',
      orders: [],
      selected: null,     // from list
      detail: null,       // /api/orders/:oid payload
      queueError: '',
      detailError: '',
      uploadError: '',
      uploadOk: '',
      loadingQueue: false,
      loadingDetail: false,
      docsLoading: false,
      uploading: false,
      markingDone: false,

      upload: {
        docType: 'PACKAGING_DOC',
        note: '',
        fileName: '',
        byteSize: 0,
        dataUrl: ''
      },

      docs: [] // order docs
    }
  },
  computed: {
    dispatchDocs () {
      var out = []
      for (var i = 0; i < (this.docs || []).length; i++) {
        var d = this.docs[i]
        if ((d.portal || '').toUpperCase() === 'DISPATCH') out.push(d)
      }
      return out
    },

    detailStage () {
      if (!this.detail || !this.detail.order) return ''
      const st = String(this.detail.order.stage || '').toUpperCase()

      // If dispatch done event exists, show helper text
      if (st === 'DISPATCH_PENDING' && this.detail.dispatchDone) {
        return 'DISPATCH_DONE (Awaiting SECOND_40)'
      }

      return st
    },

    canMarkDone () {
      var st = ''
      if (this.detail && this.detail.order && this.detail.order.stage) st = this.detail.order.stage
      else if (this.selected && this.selected.stage) st = this.selected.stage
      st = (st || '').toUpperCase()

      if (st !== 'DISPATCH_PENDING') return false
      if (this.dispatchDocs.length <= 0) return false
      return true
    },
    markDoneHint () {
      var st = ''
      if (this.detail && this.detail.order && this.detail.order.stage) st = this.detail.order.stage
      else if (this.selected && this.selected.stage) st = this.selected.stage
      st = (st || '').toUpperCase()

      if (st && st !== 'DISPATCH_PENDING') return 'Order is not in DISPATCH_PENDING stage'
      if (this.dispatchDocs.length <= 0) return 'Upload at least one dispatch doc first'
      return 'Mark dispatch done'
    }
  },
  created () {
    this.loadQueue()

    // deep-link ?open=123
    var open = null
    if (this.$route && this.$route.query && this.$route.query.open) {
      open = parseInt(this.$route.query.open, 10)
      if (!isNaN(open) && open > 0) {
        this.tryAutoOpen(open)
      }
    }
  },
  methods: {
    fmtDT (v) {
      if (!v) return '—'
      try {
        var d = new Date(v)
        if (isNaN(d.getTime())) return String(v)
        return d.toLocaleString()
      } catch (e) {
        return String(v)
      }
    },
    prettyBytes (n) {
      try {
        n = Number(n || 0)
        if (!n) return '0 B'
        var units = ['B', 'KB', 'MB', 'GB']
        var u = 0
        while (n >= 1024 && u < units.length - 1) { n = n / 1024; u++ }
        return (Math.round(n * 10) / 10) + ' ' + units[u]
      } catch (e) {
        return String(n || 0)
      }
    },

    refreshAll () {
      this.loadQueue()
      if (this.selected) this.loadDetail(this.selected.id)
    },

    async loadQueue () {
      this.queueError = ''
      this.loadingQueue = true
      try {
        var params = { stage: 'DISPATCH_PENDING' }
        if ((this.search || '').trim()) params.q = (this.search || '').trim()
        var res = await this.$api.get('/orders', { params: params })
        this.orders = Array.isArray(res && res.data) ? res.data : []


        // keep selection if still exists
        if (this.selected) {
          var keep = null
          for (var i = 0; i < this.orders.length; i++) {
            if (this.orders[i].id === this.selected.id) { keep = this.orders[i]; break }
          }
          if (keep) {
            this.selected = keep
          } else {
            this.selected = null
            this.detail = null
            this.docs = []
          }
        }
      } catch (e) {
        this.queueError = this.pickErr(e, 'Failed to load dispatch queue')
      } finally {
        this.loadingQueue = false
      }
    },

    async tryAutoOpen (oid) {
      // load queue first, then select if found; else directly load detail
      try {
        await this.loadQueue()
        var found = null
        for (var i = 0; i < this.orders.length; i++) {
          if (this.orders[i].id === oid) { found = this.orders[i]; break }
        }
        if (found) {
          this.selectOrder(found)
        } else {
          // allow viewing even if not in queue, but backend may block dispatch actions
          this.selected = { id: oid, stage: '' }
          this.loadDetail(oid)
        }
      } catch (e) {
        // ignore
      }
    },

    selectOrder (o) {
      this.selected = o
      this.detail = null
      this.docs = []
      this.uploadError = ''
      this.uploadOk = ''
      this.loadDetail(o.id)
    },

    async loadDetail (oid) {
      this.detailError = ''
      this.loadingDetail = true
      try {
        var res = await this.$api.get('/orders/' + oid)
        this.detail = (res && res.data) ? res.data : null
        await this.loadDocs(oid)
      } catch (e) {
        this.detailError = this.pickErr(e, 'Failed to load order detail')
      } finally {
        this.loadingDetail = false
      }
    },

    async loadDocs (oid) {
      this.docsLoading = true
      try {
        var res = await this.$api.get('/orders/' + oid + '/docs')
        this.docs = Array.isArray(res && res.data) ? res.data : []
      } catch (e) {
        this.detailError = this.pickErr(e, 'Failed to load order docs')
      } finally {
        this.docsLoading = false
      }
    },

    onPickFile (ev) {
      this.uploadError = ''
      this.uploadOk = ''
      this.upload.dataUrl = ''
      this.upload.fileName = ''
      this.upload.byteSize = 0

      var f = ev && ev.target ? ev.target.files && ev.target.files[0] : null
      if (!f) return

      this.upload.fileName = f.name
      this.upload.byteSize = f.size || 0

      var reader = new FileReader()
      var self = this
      reader.onload = function (e) {
        self.upload.dataUrl = e.target.result
      }
      reader.onerror = function () {
        self.uploadError = 'Failed to read file'
      }
      reader.readAsDataURL(f)
    },

    async uploadDoc () {
      if (!this.selected || !this.selected.id) return
      if (!this.upload.dataUrl) {
        this.uploadError = 'Please choose a file'
        return
      }

      this.uploading = true
      this.uploadError = ''
      this.uploadOk = ''
      try {
        await this.$api.post('/orders/' + this.selected.id + '/docs', {
          portal: 'DISPATCH',
          docType: (this.upload.docType || 'OTHER'),
          note: (this.upload.note || null),
          filename: (this.upload.fileName || 'file.bin'),
          dataUrl: this.upload.dataUrl
        })

        this.uploadOk = 'Uploaded'
        this.upload.note = ''
        this.upload.dataUrl = ''
        this.upload.fileName = ''
        this.upload.byteSize = 0

        await this.loadDocs(this.selected.id)
      } catch (e) {
        this.uploadError = this.pickErr(e, 'Upload failed')
      } finally {
        this.uploading = false
      }
    },

    async markDispatchDone () {
      if (!this.selected || !this.selected.id) return

      this.markingDone = true
      this.detailError = ''

      try {
        await this.$api.post('/orders/' + this.selected.id + '/dispatch/mark_done', {})

        // ✅ Remove from queue immediately (better UX)
        const doneId = this.selected.id
        this.orders = (this.orders || []).filter(o => o.id !== doneId)

        // ✅ Clear right panel
        this.selected = null
        this.detail = null
        this.docs = []

        // ✅ Optional: lightweight success message
        this.uploadOk = 'Dispatch marked done. Finance notified for SECOND_40.'

      } catch (e) {
        this.detailError = this.pickErr(e, 'Failed to mark dispatch done')
      } finally {
        this.markingDone = false
      }
    },


    async downloadDoc (d) {
      // /api/orders/docs/<doc_id>/download (needs Authorization header)
      if (!d || !d.id) return
      try {
        var token = localStorage.getItem('edda_token')
        var res = await fetch('/api/orders/docs/' + d.id + '/download', {
          method: 'GET',
          headers: { Authorization: 'Bearer ' + token }
        })
        if (!res.ok) throw new Error('http_' + res.status)

        var blob = await res.blob()
        var a = document.createElement('a')
        var url = window.URL.createObjectURL(blob)
        a.href = url
        a.download = d.fileName || ('order_doc_' + d.id)
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        window.URL.revokeObjectURL(url)
      } catch (e) {
        this.detailError = 'Download failed'
      }
    },

    pickErr (e, fallback) {
      try {
        if (e && e.payload && e.payload.error) return e.payload.error
        if (e && e.response && e.response.data && e.response.data.error) return e.response.data.error
        if (e && e.message) return e.message
      } catch (ex) {}
      return fallback || 'failed'
    }
  }
}
</script>

<style scoped>
.ops-page { padding: 18px; }
.ops-head { display:flex; justify-content:space-between; gap:14px; align-items:flex-end; margin-bottom:14px; }
.ops-kicker { font-size:12px; opacity:.7; }
.ops-title { margin:0; font-size:22px; }
.ops-sub { margin:6px 0 0; opacity:.75; font-size:13px; max-width:520px; }

.ops-grid { display:grid; grid-template-columns: 360px 1fr; gap:14px; align-items:start; }
@media (max-width: 980px) { .ops-grid { grid-template-columns: 1fr; } }

.card { background:#fff; border:1px solid rgba(0,0,0,.08); border-radius:14px; box-shadow:0 6px 20px rgba(0,0,0,.04); overflow:hidden; }
.card-head { padding:14px; border-bottom:1px solid rgba(0,0,0,.06); display:flex; justify-content:space-between; gap:12px; align-items:center; }
.card-title { font-weight:700; }
.card-sub { font-size:12px; opacity:.75; margin-top:4px; }

.queue-tools { display:flex; gap:8px; align-items:center; }
.inp { border:1px solid rgba(0,0,0,.12); border-radius:10px; padding:10px 10px; font-size:13px; outline:none; min-width: 220px; }
@media (max-width: 980px) { .inp { min-width: 0; width: 100%; } }

.btn { border:1px solid rgba(0,0,0,.12); background:#fff; padding:10px 12px; border-radius:10px; cursor:pointer; font-size:13px; }
.btn:disabled { opacity:.55; cursor:not-allowed; }
.btn--soft { background: rgba(0,0,0,.03); }
.btn--primary { background:#111; color:#fff; border-color:#111; }

.alert { margin:12px; padding:10px 12px; border-radius:10px; font-size:13px; }
.alert--danger { background: rgba(255,0,0,.06); border:1px solid rgba(255,0,0,.15); }
.alert--ok { background: rgba(0,200,120,.08); border:1px solid rgba(0,200,120,.16); }

.queue-list { padding:10px; display:flex; flex-direction:column; gap:8px; max-height: calc(100vh - 220px); overflow:auto; }
.queue-item { text-align:left; border:1px solid rgba(0,0,0,.08); background:#fff; border-radius:12px; padding:10px; cursor:pointer; }
.queue-item:hover { border-color: rgba(0,0,0,.18); }
.queue-item--active { border-color:#111; box-shadow: 0 0 0 3px rgba(0,0,0,.08); }
.qi-top { display:flex; justify-content:space-between; align-items:center; }
.qi-id { font-weight:800; }
.qi-stage { font-size:12px; opacity:.8; }
.qi-name { margin-top:6px; font-weight:700; }
.qi-meta { margin-top:6px; display:flex; gap:6px; flex-wrap:wrap; }
.qi-chip { font-size:12px; opacity:.75; background: rgba(0,0,0,.04); padding:4px 8px; border-radius:999px; }
.qi-time { margin-top:8px; font-size:12px; opacity:.65; }

.pill { display:inline-block; font-size:12px; padding:3px 8px; border-radius:999px; background: rgba(0,0,0,.06); }
.pill--stage { background: rgba(0,0,0,.08); font-weight:700; }

.detail-actions { display:flex; gap:8px; align-items:center; }

.order-summary { padding:14px; display:grid; grid-template-columns: 1fr 1fr; gap:10px 14px; }
@media (max-width: 980px) { .order-summary { grid-template-columns: 1fr; } }
.os-row { display:flex; justify-content:space-between; gap:10px; border:1px solid rgba(0,0,0,.06); border-radius:12px; padding:10px; background: rgba(0,0,0,.02); }
.os-label { font-size:12px; opacity:.75; }
.os-value { font-weight:700; }

.section { padding:14px; border-top:1px solid rgba(0,0,0,.06); }
.section-head { margin-bottom:10px; }
.section-title { font-weight:800; }
.section-sub { margin-top:4px; font-size:12px; opacity:.75; }

.upload-grid { display:grid; grid-template-columns: 220px 1fr; gap:10px; align-items:end; }
@media (max-width: 980px) { .upload-grid { grid-template-columns: 1fr; } }
.field { display:flex; flex-direction:column; gap:6px; }
.lbl { font-size:12px; opacity:.75; }
.field--file input[type="file"] { font-size:13px; }
.file-meta { display:flex; gap:8px; flex-wrap:wrap; }
.file-chip { font-size:12px; background: rgba(0,0,0,.05); padding:4px 8px; border-radius:999px; }
.field--btn { display:flex; justify-content:flex-end; }

.docs-list { display:flex; flex-direction:column; gap:8px; }
.doc-row { display:flex; justify-content:space-between; align-items:center; gap:10px; border:1px solid rgba(0,0,0,.08); border-radius:12px; padding:10px; }
.doc-title { display:flex; gap:8px; align-items:center; flex-wrap:wrap; }
.doc-pill { font-size:12px; background: rgba(0,0,0,.06); padding:4px 8px; border-radius:999px; font-weight:700; }
.doc-name { font-weight:700; }
.doc-meta { margin-top:4px; font-size:12px; opacity:.7; display:flex; gap:8px; flex-wrap:wrap; align-items:center; }
.dot { opacity:.5; }

.completion-box { border:1px solid rgba(0,0,0,.08); border-radius:14px; padding:12px; background: rgba(0,0,0,.02); }
.completion-row { display:flex; justify-content:space-between; align-items:center; padding:8px 0; border-bottom:1px dashed rgba(0,0,0,.12); }
.completion-row:last-child { border-bottom:none; }
.completion-row--btn { border-bottom:none; padding-top:12px; }
.completion-label { font-size:13px; opacity:.8; }
.completion-value { font-weight:900; }
.ok { color: #0a7; }
.bad { color: #c22; }

.empty { padding:18px; text-align:center; opacity:.85; }
.empty.small { padding:12px; }
.empty-title { font-weight:900; }
.empty-sub { margin-top:6px; font-size:13px; opacity:.75; }
.muted { opacity:.7; font-size:13px; }
</style>
